<template>
    <div
        class="license-plate"
        :class="[{[`country-${formattedCountryCode?.toLowerCase()}`]: formattedCountryCode, [`canton-${cantonCode?.toLowerCase()}`]: cantonCode }]"
        :data-license-plate-prefix="licensePlateInitialChars"
    >
        <template v-if="['CH', 'FL'].includes(formattedCountryCode)"><span class="initial-chars">{{ licensePlateInitialChars }}</span>{{ licensePlate.slice(2) }}</template>
        <template v-else>{{ licensePlate }}</template>
    </div>
</template>

<script>
const swissCantonCodes = ['AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR', 'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG', 'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH']

export default {
    name: 'LicensePlate',
    props: {
        countryCode: {
            type: String,
            default: '',
        },
        licensePlate: {
            type: String,
            required: true,
        },
    },
    data () {
        return {}
    },
    computed: {
        licensePlateInitialChars () {
            return this.licensePlate.slice(0, 2).toUpperCase()
        },

        assumedCountryCode () {
            if (swissCantonCodes.includes(this.licensePlateInitialChars)) {
                return 'CH'
            }
            else if (this.licensePlateInitialChars === 'FL') {
                return 'FL'
            }

            return null
        },

        formattedCountryCode () {
            return this.countryCode.toUpperCase() || this.assumedCountryCode
        },

        cantonCode () {
            if (this.formattedCountryCode && swissCantonCodes.includes(this.licensePlateInitialChars)) {
                return this.licensePlateInitialChars
            }

            return null
        },
    },
}
</script>

<style lang="scss" scoped>
// General styling of number plates
.license-plate {
    --size-coat-of-arms-width: 8px;
    --color-text: var(--color-text-primary);
    --color-background: var(--color-white);

    display: inline-block;
    position: relative;
    margin-top: -2px;
    padding: ($sizeSpacingXs / 2) $sizeSpacingXs;

    border: 1px solid var(--color-border-secondary);
    border-radius: $sizeSpacingXs;
    background-color: var(--color-background);
    box-shadow: $defaultBoxShadow;

    font-size: var(--size-text-xs);
    font-weight: bold;
    white-space: nowrap;
    color: var(--color-text);

    // Country specific styling

    // Switzerland
    $swissCantonCodes: AG, AI, AR, BE, BL, BS, FR, GE, GL, GR, JU, LU, NE, NW, OW, SG, SH, SO, SZ, TG, TI, UR, VD, VS, ZG, ZH;

    &.country-ch {
        padding-left: calc(var(--size-coat-of-arms-width) * 2);
        padding-right: calc(var(--size-coat-of-arms-width) * 2);

        &::before,
        &::after {
            content: "";
            position: absolute;
            width: calc(var(--size-coat-of-arms-width));
            height: 100%;

            background: 50% 50% no-repeat;
            background-size: calc(var(--size-coat-of-arms-width));
        }

        &::before {
            left: calc(var(--size-coat-of-arms-width) / 2);
            top: 0;
            // TODO: Find better solution for ../../ path
            background-image: url("../../assets/img/coat_of_arms/CH.svg");
        }

        &::after {
            right: calc(var(--size-coat-of-arms-width) / 2);
            top: 0;
        }

        // Coat of arms of individual cantons
        @each $canton in $swissCantonCodes {
            &.canton-#{to-lower-case($canton)} {
                &::after {
                    // TODO: Find better solution for ../../ path
                    background-image: url("../../assets/img/coat_of_arms/#{to-upper-case($canton)}.svg");
                }
            }
        }
    }

    &.country-fl {
        --color-text: var(--color-white);
        --color-background: var(--color-text-primary);

        .initial-chars {
            padding-right: calc(var(--size-coat-of-arms-width) * 1.5);
            background: url("../../assets/img/coat_of_arms/FL.svg") no-repeat 95% 50%;
            background-size: calc(var(--size-coat-of-arms-width));
        }
    }

    // TODO: Re-implement this if there should be non-CH cars in the system
    // Countries with European number plates
    $euroNumberPlateCountries: (
        AT: "A",
        BE: "B",
        BG: "BG",
        CY: "CY",
        CZ: "CZ",
        DE: "D",
        DK: "DK",
        EE: "EST",
        ES: "E",
        FI: "FIN",
        FR: "F",
        GB: "GB",
        GR: "GR",
        HR: "HR",
        HU: "H",
        IE: "IRL",
        IT: "I",
        LT: "LT",
        LU: "L",
        LV: "LV",
        MT: "M",
        NL: "NL",
        PL: "PL",
        PT: "P",
        RO: "RO",
        SE: "S",
        SI: "SLO",
        SK: "SK",
    );

    // TODO improvement: Tidy up code so that less code is duplicated in loop
    @each $country, $abbreviation in $euroNumberPlateCountries {
        $sizeCountryAbbr: 15px;
        $colorCountryBackground: #039;

        &.country-#{to-lower-case($country)} {
            padding-left: pxToRem($sizeCountryAbbr + 5);

            &::before {
                content: "#{$abbreviation}";
                position: absolute;
                top: 0;
                left: 0;
                width: pxToRem($sizeCountryAbbr);
                height: 100%;
                padding: 0 pxToRem(2);

                border-radius: pxToRem(3) 0 0 pxToRem(3);
                // TODO: Find better solution for ../../ path
                background: $colorCountryBackground url("../../assets/img/flag_of_europe.svg") 50% 1px no-repeat;
                background-size: pxToRem($sizeCountryAbbr);

                color: var(--color-white);
                font-size: pxToRem(6);
                line-height: pxToRem(32);
                text-align: center;
            }
        }
    }
}
</style>
